import { ChangeEvent } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import {
  Box,
  Checkbox,
  checkboxClasses,
  FormHelperText,
  SxProps,
  Theme,
  Typography,
} from '@mui/material'

import { DefaultElementProps } from 'common/interfaces'
import { useCompany } from 'utils/company'
import { useFlowSlug } from 'utils/flow'
import { useLocalStorage } from 'utils/hook'

import { ConditionalAgreementBlock as ConditionalAgreementBlockProps } from './interface'

const style: SxProps<Theme> = {
  display: 'flex',
  '& a': {
    color: 'primary.main',
    textDecoration: 'none',
  },
  [`& .${checkboxClasses.root}`]: {
    marginRight: '4px',
    marginLeft: '-6px',
  },
}

export default function ConditionalAgreementBlock({
  name,
  label,
  termsAndConditionsLink,
  privacyPolicyLink,
}: ConditionalAgreementBlockProps & DefaultElementProps): JSX.Element {
  const { control } = useFormContext()
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: { required: true },
  })

  const flowSlug = useFlowSlug()
  const [lsValue, setLS] = useLocalStorage(`${flowSlug}/metadata`, {})

  const { data: company } = useCompany()

  const termsAndConditionsLinkSource = termsAndConditionsLink ?? company?.terms_and_conditions_link
  const privacyPolicyLinkSource = privacyPolicyLink ?? company?.privacy_policy_link

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    field.onChange(e)

    if (e.target.checked) {
      setLS({
        ...lsValue,
        terms_and_conditions: termsAndConditionsLinkSource,
        privacy_policy: privacyPolicyLinkSource,
        user_date_agreement: new Date(),
      })
    }
  }

  return (
    <Box className="agreement-wrap">
      <Box sx={style}>
        <Checkbox {...field} onChange={handleChange} />
        <Typography>
          {label}
          <a href={termsAndConditionsLinkSource} target="_blank" rel="noreferrer">
            {' '}
            Terms & Conditions{' '}
          </a>
          and
          <a href={privacyPolicyLinkSource} target="_blank" rel="noreferrer">
            {' '}
            Privacy Policy
          </a>
          .
        </Typography>
      </Box>
      {error !== undefined && (
        <FormHelperText sx={{ color: 'red' }}>
          Please indicate that you have read and agree to the Terms and Conditions and Privacy
          Policy
        </FormHelperText>
      )}
    </Box>
  )
}
